import { useMemo } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { UIComponentData } from 'tripPlanner/components/Chat/Message'
import {
  MARKER_TYPE_SUGGESTED,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_SOURCE_SUGGESTED,
  MARKER_SOURCE_ITINERARY,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import type { TripStop } from 'tripPlanner/api/types'
import type { MapPathComponent, MapRegionComponent } from 'tripPlanner/components/Chat/chatMapConstants'
import { buildItemMarker, getItemCoords } from './itineraryMapMarkers'
import type { extractItineraryMarkers } from './itineraryMapMarkers'

type MapMarker = MapMarkerComponent

// Helper to check if data is location_entities type
function isLocationEntitiesType(data: UIComponentData): boolean {
  return data.type === 'location_entities'
}

// Helper to safely get places from location_entities data
function getLocationPlaces(data: UIComponentData): Array<{
  name?: string
  lat?: number | null
  lng?: number | null
  googlePlaceId?: string | null
  formattedAddress?: string | null
  editorialSummary?: string | null
}> | undefined {
  if (data.type === 'location_entities' && 'places' in data) {
    return data.places
  }
  return undefined
}

interface MapData {
  markers: Array<MapMarker>
  paths?: Array<MapPathComponent>
  regions?: Array<MapRegionComponent>
}

interface UseMapDataArgs {
  messages: Array<MessageItem>
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  itineraryResult: ReturnType<typeof extractItineraryMarkers>
}

/**
 * Extracts map data from assistant messages.
 * Only handles location_entities - hotel markers are now managed by useSearchResults.
 * Uses the most recent assistant message that contains location_entities.
 */
function extractMapDataFromRecentMessage(messages: Array<MessageItem>): MapData | undefined {
  const assistantMessages = messages.filter(msg => msg.role === 'assistant')
  if (assistantMessages.length === 0) return undefined

  for (let i = assistantMessages.length - 1; i >= 0; i -= 1) {
    const message = assistantMessages[i]
    const locationEntitiesPart = message.parts.find(
      part => part.type === 'ui_component' && isLocationEntitiesType(part.data),
    )

    if (locationEntitiesPart?.type === 'ui_component' && isLocationEntitiesType(locationEntitiesPart.data)) {
      const places = getLocationPlaces(locationEntitiesPart.data)

      if (places && Array.isArray(places)) {
        const seenPlaceIds = new Set<string>()
        const markers = places
          .filter(place => {
            if (typeof place.lat !== 'number' || typeof place.lng !== 'number') return false
            if (place.googlePlaceId) {
              if (seenPlaceIds.has(place.googlePlaceId)) return false
              seenPlaceIds.add(place.googlePlaceId)
            }
            return true
          })
          .map(place => ({
            lat: place.lat!,
            lng: place.lng!,
            label: place.name,
            placeId: place.googlePlaceId ?? undefined,
            description: (place.editorialSummary ?? place.formattedAddress) ?? undefined,
            markerType: MARKER_TYPE_SUGGESTED,
            source: MARKER_SOURCE_SUGGESTED,
          }))

        if (markers.length === 0) continue

        return { markers }
      }
    }
  }

  return undefined
}

// Stable reference for the default empty-map case so downstream memos don't see
// a new object on every render when no map data is available.
const EMPTY_MAP_DATA: MapData = { markers: [] }

/**
 * Hook that manages map data extraction and computation.
 * Extracts map markers from messages and itinerary data.
 *
 * Priority order:
 * 1. Selected day items (highest — isolated day view)
 * 2. All itinerary stops and items
 * 3. Location entities from the latest assistant message (fallback for new chats)
 *
 * Each priority level is independently memoized so the returned mapData
 * reference stays stable when a lower-priority source changes (e.g., message
 * streaming text deltas don't destabilize the itinerary-derived map data).
 *
 * Hotel markers are managed separately by useSearchResults and combined
 * in ChatTripPlannerPage.
 */
export function useMapData({
  messages,
  selectedDayItineraryId,
  selectedStop,
  itineraryResult,
}: UseMapDataArgs) {
  const selectedDayItems = useMemo(() => {
    if (!selectedDayItineraryId || !selectedStop) {
      return []
    }
    const day = selectedStop.days.find((d) => d.id === selectedDayItineraryId)
    return day?.items ?? []
  }, [selectedDayItineraryId, selectedStop])

  // Priority 1: Day items when viewing a specific day itinerary (isolated view).
  // Only changes when the selected day or stop changes.
  const dayItemsMapData = useMemo((): MapData | undefined => {
    if (!selectedDayItineraryId || selectedDayItems.length === 0) return undefined
    const markers: Array<MapMarker> = []
    selectedDayItems.forEach((item) => {
      const coords = getItemCoords(item)
      if (coords) markers.push(buildItemMarker(item, coords, selectedStop!.id, selectedDayItineraryId))
    })
    return markers.length > 0 ? { markers } : undefined
  }, [selectedDayItineraryId, selectedDayItems, selectedStop])

  // Priority 2: All itinerary stops and items.
  // Only changes when itineraryData changes (enrichment, add/remove stop, etc.).
  const itineraryMapData = useMemo((): MapData | undefined => {
    return itineraryResult.markers.length > 0 ? { markers: itineraryResult.markers } : undefined
  }, [itineraryResult])

  // Priority 3: Location entities from the latest assistant message.
  // Changes on every message update (streaming text deltas), but is only
  // used as a fallback when no itinerary or day items are available.
  const locationEntitiesMapData = useMemo(() => {
    return extractMapDataFromRecentMessage(messages)
  }, [messages])

  // Combine priorities — day items take precedence (isolated view).
  // When both itinerary and location entities exist, merge them. Deduplicates by
  // placeId and label. Hotels in the itinerary that lack coordinates are "unresolved":
  // if a matching location_entities marker exists, it's upgraded to a chat-hotel marker.
  const mapData = useMemo((): MapData => {
    if (dayItemsMapData) return dayItemsMapData

    if (itineraryMapData && locationEntitiesMapData) {
      const itineraryPlaceIds = new Set(
        itineraryMapData.markers.map(m => m.placeId).filter(Boolean),
      )
      const itineraryLabels = new Set(
        itineraryMapData.markers.map(m => m.label?.toLowerCase()).filter(Boolean),
      )
      const { unresolvedHotels } = itineraryResult
      const extraMarkers: Array<MapMarker> = []
      locationEntitiesMapData.markers.forEach(m => {
        if (m.placeId && itineraryPlaceIds.has(m.placeId)) return
        if (m.label && itineraryLabels.has(m.label.toLowerCase())) return

        const offerId = m.label ? unresolvedHotels.get(m.label.toLowerCase()) : undefined
        if (offerId) {
          extraMarkers.push({
            ...m,
            markerType: MARKER_TYPE_CHAT_HOTEL,
            source: MARKER_SOURCE_ITINERARY,
            offerId,
            markerId: offerId,
          })
        } else {
          extraMarkers.push(m)
        }
      })

      if (extraMarkers.length > 0) {
        return { markers: [...itineraryMapData.markers, ...extraMarkers] }
      }
      return itineraryMapData
    }

    if (!itineraryMapData && locationEntitiesMapData) {
      const { unresolvedHotels } = itineraryResult
      if (unresolvedHotels.size > 0) {
        const upgraded = locationEntitiesMapData.markers.map(m => {
          const offerId = m.label ? unresolvedHotels.get(m.label.toLowerCase()) : undefined
          if (offerId) {
            return {
              ...m,
              markerType: MARKER_TYPE_CHAT_HOTEL,
              source: MARKER_SOURCE_ITINERARY,
              offerId,
              markerId: offerId,
            }
          }
          return m
        })
        return { markers: upgraded }
      }
    }

    return itineraryMapData ?? locationEntitiesMapData ?? EMPTY_MAP_DATA
  }, [dayItemsMapData, itineraryMapData, locationEntitiesMapData, itineraryResult])

  const hasMapMarkers = mapData.markers && mapData.markers.length > 0

  return {
    mapData,
    hasMapMarkers,
    selectedDayItems,
  }
}
